import { CommonModule, CurrencyPipe } from '@angular/common'
import { Component, Input, OnInit } from '@angular/core'
import { FormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { DatabaseService } from '@app/modules/database/services/database.service'
import { NbCheckboxModule, NbToggleModule, NbTooltipModule } from '@nebular/theme'
import { fadeInOnEnterAnimation, fadeOutOnLeaveAnimation } from 'angular-animations'
/**
 * Defines a financial record in an unsettled export order
 */
export interface Record {
  /**
   * Whether the record can be activated (Only in OrderByForms) @see OrderByForms
   */
  canBeActivated: boolean
  /**
   * Is the record active?
   */
  active: boolean
  /**
   * Record title
   */
  title: string
  /**
   * Amount of selected registrations
   */
  selected?: number
  /**
   * Amount of settled registrations
   */
  settled?: number
  /**
   * Amount of unsettled registrations
   */
  unsettled?: number
  /**
   * price for each registration at current plan
   */
  eachAt?: number
  /**
   * price for each registration at Pro plan
   */
  eachAtCompared?: number
  /**
   * record order id used to remove an order segment based on activation
   */
  id?: number
  /**
   * Total price for record on current plan
   */
  price: number
  /**
   * Record breakdown - Multiple records
   */
  breakdown?: Record[]
  /**
   * Toggles breakdown mode (On settle by registration default is true) @see OrderByRegistrations
   */
  viewBreakdown?: boolean
  /**
   * Total price for record on Pro plan
   */
  priceCompared: number
}

/**
 * Displays a table of order records with the ability to toggle several records in the case of @see OrderByForms
 */
@Component({
  selector: 'ngx-settle-table',
  templateUrl: './settle-table.component.html',
  styleUrls: ['./settle-table.component.scss'],
  animations: [fadeInOnEnterAnimation({ duration: 300, delay: 0 }), fadeOutOnLeaveAnimation({ duration: 300, delay: 0 })],
  imports: [CommonModule, CurrencyPipe, FormsModule, MatIconModule, NbCheckboxModule, NbToggleModule, NbTooltipModule],
  standalone: true
})
export class SettleTableComponent implements OnInit {
  /**
   * Records array
   */
  @Input()
  records: Record[]
  /**
   * Registrant mode? @see OrderByRegistrations @see OrderByForms @see registrantMode in @see SettleRegistrantsComponent
   */
  @Input()
  registrantMode = false

  @Input()
  settledForms = []

  @Input()
  settledTotal
  /**
   * Current plan
   */
  @Input()
  plan: string
  /**
   * Total record (final calculations based on selection)
   */
  total: Record

  /**
   * Form array that contains personas controls
   */
  formArray: UntypedFormArray
  /**
   * Creates the componet
   */
  constructor(private service: DatabaseService, private fb: UntypedFormBuilder) {}

  /**
   * Calculates the total record on initialization
   */
  ngOnInit(): void {
    this.calculateTotal()
    this.formArray = this.service.forms
    this.limitFormsToRecords()
    this.appendRecordsToForms()
  }

  /**
   * Filter out forms that don't have records
   */
  limitFormsToRecords() {
    if (this.formArray) this.formArray = this.fb.array(this.formArray?.controls.filter((o) => this.records.map((o) => o.id).includes(o.value.id)))
  }

  /**
   * Appends each record to its persona
   */
  appendRecordsToForms() {
    if (this.formArray)
      this.formArray.controls.forEach((o) => {
        const record = this.records.find((record) => record.id === o.value.id)
        o.patchValue({ ...o.value, record: record })
      })
  }
  /**
   * Toggles a record active state then re-calculates the total record
   * @param record Record
   */
  toggle(record: Record) {
    record.active = !record.active
    this.calculateTotal()
  }
  /**
   * Calculates the total record
   */
  calculateTotal() {
    this.total = {
      canBeActivated: false,
      active: false,
      title: 'Total',
      selected: this.records.filter((o) => o.active).reduce((p, c) => p + (c.selected || 0), 0),
      settled: this.records.filter((o) => o.active).reduce((p, c) => p + (c.settled || 0), 0),
      unsettled: this.records.filter((o) => o.active).reduce((p, c) => p + (c.unsettled || 0), 0),
      price: this.records.filter((o) => o.active).reduce((p, c) => p + c.price, 0),
      priceCompared: this.records.filter((o) => o.active).reduce((p, c) => p + c.priceCompared, 0)
    }
  }

  /**
   * Checks if auto_settle is set for the persona
   * @param personaName
   * @returns FormControl of
   */
  isFormAutoSettle(personaName: string) {
    return this.formArray.controls.find((o: UntypedFormGroup) => o.value.name === personaName)
  }

  /**
   * Toggle event handler
   * @param event
   * @param personaName
   */
  updateToggleValue(event, personaName: string) {
    const form = this.formArray.controls.find((o) => o.value.name === personaName)
    form.value['options']['auto_settle'] = event
    form.markAsDirty()
    this.service.forms = this.formArray
  }
}
